// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/News.js

import React from 'react';
import { FiExternalLink, FiCalendar } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const getImage = (source, apiBaseURL) => {
  if (!source) return '';
  return buildResolvedFile(source, apiBaseURL).resolvedSrc || '';
};

const NewsCard = ({ item, apiBaseURL }) => {
  const resolvedLogo = getImage(item.publicationLogo, apiBaseURL);
  const resolvedThumbnail = getImage(item.thumbnail || item.image || item.imageUrl, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  const newsDate = item.date ? new Date(item.date) : null;
  const formattedDate = newsDate ? newsDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }) : null;

  return (
    <div 
      className="group bg-white rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
      style={{ borderColor: `${primary}15` }}
    >
      <div className="p-6" style={{ fontFamily: fontBody }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {resolvedLogo ? (
              <img
                src={resolvedLogo}
                alt={item.publication || 'Publication'}
                className="h-8 w-auto object-contain"
                loading="lazy"
              />
            ) : (
              <span className="text-sm font-medium" style={{ color: primary }}>
                {item.publication}
              </span>
            )}
          </div>
          {formattedDate && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#9CA3AF' }}>
              <FiCalendar className="w-3 h-3" />
              <span>{formattedDate}</span>
            </div>
          )}
        </div>

        {resolvedThumbnail && (
          <div className="mb-4 overflow-hidden rounded-xl bg-gray-50 aspect-[16/10]">
            <img
              src={resolvedThumbnail}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        )}

        <h4 
          className="text-lg font-bold mb-2 transition-colors"
          style={{ 
            color: '#1F2937',
            fontFamily: fontHeading,
          }}
        >
          {item.title}
        </h4>

        {item.excerpt && (
          <p className="text-sm leading-relaxed mb-4 line-clamp-3" style={{ color: '#6B7280' }}>
            {item.excerpt}
          </p>
        )}

        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm transition-colors group/link"
            style={{ color: secondary }}
          >
            <span>Read Full Article</span>
            <FiExternalLink className="w-3 h-3 transition-transform group-hover/link:translate-x-0.5" />
          </a>
        )}
      </div>
    </div>
  );
};

const News = ({ news, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'grid', items = [] } = news || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  const gridClasses = {
    'grid': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    'list': 'grid-cols-1 max-w-4xl mx-auto gap-4',
    'cards': 'grid-cols-1 md:grid-cols-2 gap-6',
  };

  return (
    <section id="news" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                Media
              </span>
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          <div className={gridClasses[displayStyle] || gridClasses['grid']}>
            {items.map((item) => (
              <NewsCard 
                key={item._id || item.id || item.title} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default News;